import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import ProPaywall from '@/components/ProPaywall';
import {
  BarChart3,
  TrendingUp,
  Eye,
  Package,
  FileText,
  ShoppingCart,
  Store,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  Lock,
  Sparkles
} from 'lucide-react';
import { getFirstImage } from '@/utils/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsPage = () => {
  const { user, token } = useAuth();
  const { isUSA } = useLocale();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!token) return;
      
      try {
        const res = await fetch(`${API_URL}/api/dashboard/analytics`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.status === 403) {
          setShowPaywall(true);
          setLoading(false);
          return;
        }
        
        if (res.ok) {
          const data = await res.json();
          setAnalytics(data);
        } else {
          setError('Error loading analytics');
        }
      } catch (e) {
        setError('Error loading analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  // Show paywall for non-PRO users
  if (showPaywall || !analytics) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="page-header mb-8">
          <h1 className="page-title flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            Analytics
          </h1>
        </div>

        <Card className="border-2 border-dashed border-slate-300 bg-slate-50">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-slate-400" />
            </div>
            <h2 className="text-xl font-bold text-slate-700 mb-2">
              {isUSA ? 'Analytics is a PRO feature' : 'Analytics es una función PRO'}
            </h2>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              {isUSA 
                ? 'Upgrade to PRO to see detailed analytics about your products, views, and buyer engagement.'
                : 'Actualiza a PRO para ver análisis detallados de tus productos, vistas y engagement de compradores.'}
            </p>
            <Link to="/dashboard/subscription">
              <Button className="bg-emerald-500 hover:bg-emerald-600">
                <Sparkles className="w-4 h-4 mr-2" />
                {isUSA ? 'Upgrade to PRO' : 'Actualizar a PRO'}
              </Button>
            </Link>
          </CardContent>
        </Card>

        <ProPaywall 
          isOpen={showPaywall} 
          onClose={() => setShowPaywall(false)} 
          feature="Analytics"
          reason="default"
        />
      </div>
    );
  }

  const { summary, recent_activity, top_products, quotes_by_status, store } = analytics;

  return (
    <div data-testid="analytics-page">
      <div className="page-header mb-8">
        <h1 className="page-title flex items-center gap-2">
          <BarChart3 className="w-6 h-6" />
          Analytics
        </h1>
        <p className="page-subtitle">
          {isUSA ? 'Track your performance and engagement' : 'Rastrea tu rendimiento y engagement'}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{isUSA ? 'Products' : 'Productos'}</p>
                <p className="text-2xl font-bold text-slate-900">{summary.total_products}</p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{isUSA ? 'Total Views' : 'Vistas Totales'}</p>
                <p className="text-2xl font-bold text-slate-900">{summary.total_views.toLocaleString()}</p>
              </div>
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{isUSA ? 'Inquiries' : 'Cotizaciones'}</p>
                <p className="text-2xl font-bold text-slate-900">{summary.total_quotes}</p>
              </div>
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{isUSA ? 'Conversion' : 'Conversión'}</p>
                <p className="text-2xl font-bold text-slate-900">{summary.conversion_rate}%</p>
              </div>
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {isUSA ? 'Recent Activity' : 'Actividad Reciente'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-600">
                    {isUSA ? 'Inquiries (7 days)' : 'Cotizaciones (7 días)'}
                  </span>
                </div>
                <Badge variant="outline" className="font-bold">
                  {recent_activity.quotes_7d}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-600">
                    {isUSA ? 'Inquiries (30 days)' : 'Cotizaciones (30 días)'}
                  </span>
                </div>
                <Badge variant="outline" className="font-bold">
                  {recent_activity.quotes_30d}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <ShoppingCart className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-600">
                    {isUSA ? 'Orders (30 days)' : 'Pedidos (30 días)'}
                  </span>
                </div>
                <Badge variant="outline" className="font-bold">
                  {recent_activity.orders_30d}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quotes by Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {isUSA ? 'Inquiries by Status' : 'Cotizaciones por Estado'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">{isUSA ? 'New' : 'Nuevas'}</span>
                  <span className="font-medium">{quotes_by_status.nueva}</span>
                </div>
                <Progress value={quotes_by_status.nueva} max={summary.total_quotes || 1} className="h-2 [&>div]:bg-blue-500" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">{isUSA ? 'In Progress' : 'En Proceso'}</span>
                  <span className="font-medium">{quotes_by_status.en_proceso}</span>
                </div>
                <Progress value={quotes_by_status.en_proceso} max={summary.total_quotes || 1} className="h-2 [&>div]:bg-amber-500" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">{isUSA ? 'Completed' : 'Completadas'}</span>
                  <span className="font-medium">{quotes_by_status.completada}</span>
                </div>
                <Progress value={quotes_by_status.completada} max={summary.total_quotes || 1} className="h-2 [&>div]:bg-emerald-500" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">{isUSA ? 'Cancelled' : 'Canceladas'}</span>
                  <span className="font-medium">{quotes_by_status.cancelada}</span>
                </div>
                <Progress value={quotes_by_status.cancelada} max={summary.total_quotes || 1} className="h-2 [&>div]:bg-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Products */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="text-lg">
            {isUSA ? 'Top Performing Products' : 'Productos con Mejor Rendimiento'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {top_products.length > 0 ? (
            <div className="space-y-3">
              {top_products.map((product, index) => (
                <Link 
                  key={product.product_id} 
                  to={`/dashboard/products/${product.product_id}/edit`}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center text-sm font-medium text-slate-600">
                      {index + 1}
                    </span>
                    {product.images?.[0] ? (
                      <img 
                        src={getFirstImage(product.images)} 
                        alt={product.name}
                        className="w-10 h-10 rounded object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center">
                        <Package className="w-5 h-5 text-slate-400" />
                      </div>
                    )}
                    <span className="font-medium text-slate-900">{product.name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-500">
                    <Eye className="w-4 h-4" />
                    <span className="font-medium">{product.views || 0}</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">
              {isUSA ? 'No products yet' : 'Aún no tienes productos'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Store Stats */}
      {store && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Store className="w-5 h-5" />
              {isUSA ? 'Your Store' : 'Tu Tienda'}: {store.store_name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <p className="text-sm text-slate-500">{isUSA ? 'Store Views' : 'Vistas de Tienda'}</p>
                <p className="text-2xl font-bold text-slate-900">{store.views}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <p className="text-sm text-slate-500">{isUSA ? 'Products Listed' : 'Productos Listados'}</p>
                <p className="text-2xl font-bold text-slate-900">{store.products_listed}</p>
              </div>
            </div>
            <Link to="/dashboard/store" className="block mt-4">
              <Button variant="outline" className="w-full">
                {isUSA ? 'Manage Store' : 'Administrar Tienda'}
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsPage;
